import { CloseIcon, CallToolIcon, GetOffsetIcon, StopIcon } from '@/icons';
import styles from '@/components/ToolsPage/ToolActionModal.module.css';

interface ToolActionModalProps {
  /** Which operation is running — picks the header text and icon. */
  readonly action: 'call' | 'offset';
  readonly toolNo: string;
  readonly toolType: string;
  /** Stop button — aborts the in-flight MDI command on the machine. */
  readonly onStop: () => void;
  /** X button — dismiss without aborting (the op keeps running on the machine). */
  readonly onClose: () => void;
}

/**
 * Loading modal shown while a single MDI op (Call Tool / Get Offset) executes on
 * LinuxCNC. It opens on click and closes when the backend `mdiBusy` flag clears;
 * "Running..." is a live/loading indicator for the interpreter-busy interval.
 */
export function ToolActionModal({
  action,
  toolNo,
  toolType,
  onStop,
  onClose,
}: ToolActionModalProps): JSX.Element {
  const isOffset = action === 'offset';
  const HeaderIcon = isOffset ? GetOffsetIcon : CallToolIcon;
  const title = isOffset ? 'Get Offset...' : 'Calling Tool...';

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label={title}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.headerLead}>
            <HeaderIcon width={28} height={28} className={styles.headerIcon} />
            <span className={styles.title}>{title}</span>
          </div>
          <button className={styles.close} onClick={onClose} type="button" aria-label="Close">
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <div className={styles.card}>
          <div className={styles.badge}>
            <span className={styles.badgeNo}>{toolNo}</span>
            <span className={styles.badgeType}>{toolType || 'No Tool Type'}</span>
          </div>
          <div className={styles.status}>
            <div className={styles.progressTrack}>
              <span className={styles.progressBar} />
            </div>
            <span className={styles.running}>Running...</span>
          </div>
        </div>

        <button type="button" className={styles.stopBtn} onClick={onStop}>
          <StopIcon width={28} height={28} className={styles.stopIcon} />
          Stop
        </button>
      </div>
    </div>
  );
}
